import { useEffect, useRef } from 'react'

import { captchaSiteKey } from './captcha'

type TurnstileApi = {
  render: (
    el: HTMLElement,
    opts: {
      sitekey: string
      appearance?: 'always' | 'execute' | 'interaction-only'
      theme?: 'auto' | 'light' | 'dark'
      callback: (token: string) => void
      'expired-callback'?: () => void
      'error-callback'?: () => void
    }
  ) => string
  remove: (id: string) => void
  reset: (id: string) => void
}

declare global {
  interface Window {
    turnstile?: TurnstileApi
  }
}

const SCRIPT_SRC =
  'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit'

export const CAPTCHA_FAILED =
  "We couldn't confirm you're not a bot. Please reload and try again."

type Waiter = {
  resolve: (token: string) => void
  reject: (reason: Error) => void
}

let container: HTMLElement | null = null
let widgetId: string | null = null
let token = ''
let waiters: Waiter[] = []
let started: Promise<void> | null = null

let scriptPromise: Promise<void> | null = null
function loadTurnstile(): Promise<void> {
  if (scriptPromise) return scriptPromise
  scriptPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script')
    script.src = SCRIPT_SRC
    script.async = true
    script.defer = true
    script.onload = () => resolve()
    script.onerror = () => {
      scriptPromise = null
      script.remove()
      reject(new Error('failed to load turnstile'))
    }
    document.head.appendChild(script)
  })
  return scriptPromise
}

function rejectWaiters() {
  for (const waiter of waiters) waiter.reject(new Error('turnstile failed'))
  waiters = []
}

function startCaptcha(): Promise<void> {
  const siteKey = captchaSiteKey()
  const el = container
  if (!siteKey || !el) return Promise.reject(new Error('turnstile not mounted'))
  started ??= loadTurnstile()
    .then(() => {
      if (container !== el || !window.turnstile) {
        throw new Error('turnstile not mounted')
      }
      widgetId = window.turnstile.render(el, {
        sitekey: siteKey,
        appearance: 'interaction-only',
        theme: 'auto',
        callback: issued => {
          if (!waiters.length) {
            token = issued
            return
          }
          for (const waiter of waiters) waiter.resolve(issued)
          waiters = []
        },
        'expired-callback': () => {
          token = ''
        },
        'error-callback': () => {
          token = ''
          rejectWaiters()
        }
      })
    })
    .catch((error: unknown) => {
      started = null
      throw error
    })
  return started
}

async function nextToken(): Promise<string> {
  await startCaptcha()
  if (token) {
    const issued = token
    token = ''
    return issued
  }
  return new Promise((resolve, reject) => waiters.push({ resolve, reject }))
}

/** null when the check failed. tokens are single-use, so this also starts the next one. */
export async function captchaHeaders(): Promise<{
  headers?: { 'x-captcha-response': string }
} | null> {
  if (!captchaSiteKey()) return {}
  try {
    const issued = await nextToken()
    if (widgetId) window.turnstile?.reset(widgetId)
    return { headers: { 'x-captcha-response': issued } }
  } catch {
    return null
  }
}

/** cloudflare's iframe floods the console, so it waits for the first click or key. */
export function TurnstileWidget() {
  const siteKey = captchaSiteKey()
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const el = containerRef.current
    if (!siteKey || !el) return
    container = el
    const start = () => {
      void startCaptcha().catch(() => undefined)
    }
    document.addEventListener('pointerdown', start, { once: true })
    document.addEventListener('keydown', start, { once: true })
    return () => {
      document.removeEventListener('pointerdown', start)
      document.removeEventListener('keydown', start)
      if (container !== el) return
      if (widgetId) window.turnstile?.remove(widgetId)
      container = null
      widgetId = null
      token = ''
      started = null
      rejectWaiters()
    }
  }, [siteKey])

  if (!siteKey) return null
  return <div ref={containerRef} className='max-w-full overflow-x-auto' />
}
